import * as Icons from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";

import AccountEventsTable from "@/components/debug/AccountEventsTable";
import AudioEncodingsTable from "@/components/debug/AudioEncodingsTable";
import ItemFilesTable from "@/components/debug/ItemFilesTable";
import ItemLinksTable from "@/components/debug/ItemLinksTable";
import TranscriptionsTable from "@/components/debug/TranscriptionsTable";
import { db } from "@/db/db";
import { AppContext } from "@/models/AppStateProvider";
import { useReactiveRelationalQuery } from "@/models/hooks/useReactiveRelationalQuery";
import "regenerator-runtime/runtime";
import { and, eq, getTableColumns } from "drizzle-orm";

import {
  account,
  accountEvent,
  audioEncoding,
  feed as feedTable,
  file,
  item as itemTable,
  link,
  transcription,
  workspaceMembership,
} from "@/db/schema";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery";

const itemsQuery = (feedItemId: string) => db.query.item.findFirst({ where: eq(itemTable.id, feedItemId) });
const currentFeedQuery = (feedId: string) => db.query.feed.findFirst({ where: eq(feedTable.id, feedId) });
const filesQuery = (contentId: string) => db.query.file.findMany({ where: eq(file.contentId, contentId) });
const linksQuery = (contentId: string) => db.query.link.findMany({ where: eq(link.contentId, contentId) });
const audioQuery = (contentId: string) =>
  db.query.audioEncoding.findMany({ where: eq(audioEncoding.contentId, contentId) });

const accountEventQuery = (itemId: string) =>
  db
    .select({
      ...getTableColumns(accountEvent),
      accountName: account.name,
    })
    .from(accountEvent)
    .leftJoin(account, eq(accountEvent.accountId, account.id))
    .where(eq(accountEvent.itemId, itemId));

const transcriptionsQuery = (contentId: string) =>
  db.query.transcription.findMany({
    where: and(eq(transcription.contentId, contentId), eq(transcription.transcriptionType, "full")),
  });
const workspaceMembershipQuery = (workspaceId: string, accountId: string) =>
  db.query.workspaceMembership.findFirst({
    where: and(eq(workspaceMembership.workspaceId, workspaceId), eq(workspaceMembership.accountId, accountId)),
  });

function StatusChip({ status, deletedAt }: { status: string | null; deletedAt: string | null }) {
  if (deletedAt) {
    return <Chip label="Deleted" color="error" size="small" />;
  }
  switch (status) {
    case "Completed":
      return <Chip label="Completed" color="success" size="small" />;
    case "Processing":
      return <Chip label="Processing" color="warning" size="small" />;
    case "Failed":
      return <Chip label="Failed" color="error" size="small" />;
    default:
      return <Chip label={status || "Unknown"} size="small" />;
  }
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip title={copied ? "Copied!" : `Copy ${label}`}>
      <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5 }}>
        {copied ? <Icons.Check fontSize="small" /> : <Icons.ContentCopy fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function IdField({ label, value }: { label: string; value: string }) {
  const theme = useTheme();
  return (
    <Stack spacing={0.25}>
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          textTransform: "uppercase",
          fontSize: "0.65rem",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.8rem",
            color: theme.palette.text.primary,
          }}
        >
          {value}
        </Typography>
        <CopyButton text={value} label={label} />
      </Stack>
    </Stack>
  );
}

function SectionAccordion({
  title,
  count,
  defaultExpanded = false,
  children,
}: {
  title: string;
  count?: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        backgroundColor: theme.palette.background.paper,
        "&:before": { display: "none" },
        borderRadius: 1,
        "&.Mui-expanded": { margin: 0 },
      }}
    >
      <AccordionSummary expandIcon={<Icons.ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">{title}</Typography>
          {count !== undefined && <Chip label={count} size="small" variant="outlined" />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function DebugItemId({ propFeedId, propItemId }: { propFeedId?: string; propItemId?: string }) {
  const params = useParams();
  const feedId = propFeedId ?? (params?.feedId as string);
  const feedItemId = propItemId ?? (params?.itemId as string);
  const { flags } = React.useContext(AppContext);
  const theme = useTheme();
  const [showRawData, setShowRawData] = useState(false);

  const item = useReactiveRelationalQuery(itemsQuery, [feedItemId], "DebugItem:findItemById") ?? null;
  const currentFeed = useReactiveRelationalQuery(currentFeedQuery, [feedId], "DebugItem:findFeedById") ?? null;
  const files = useReactiveRelationalQuery(filesQuery, [item?.contentId ?? ""], "DebugItem:files") ?? [];
  const links = useReactiveRelationalQuery(linksQuery, [item?.contentId ?? ""], "DebugItem:links") ?? [];
  const audio = useReactiveRelationalQuery(audioQuery, [item?.contentId ?? ""], "DebugItem:audio") ?? [];
  const accountEvents =
    useReactiveSelectQuery(accountEventQuery, [feedItemId ?? ""], "DebugItem:accountEventQuery") ?? [];
  const transcriptions =
    useReactiveRelationalQuery(transcriptionsQuery, [item?.contentId ?? ""], "DebugItem:transcriptions") ?? [];
  const authorMembership = useReactiveRelationalQuery(
    workspaceMembershipQuery,
    [currentFeed?.workspaceId ?? "", item?.accountId ?? ""],
    "DebugItem:authorMembership",
  );

  const centerStyle = {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  };

  if (!currentFeed) {
    return <Stack sx={centerStyle}>Feed Not Found</Stack>;
  }

  if (!item) {
    return <Stack sx={centerStyle}>Item Not Found</Stack>;
  }

  if (!flags.debugMode) {
    return <Stack sx={centerStyle}>Debug Mode Disabled</Stack>;
  }

  const isProd = window.location.hostname?.includes("storyboard.com");
  const isLocal = window.location.hostname?.includes("localhost");
  const stage = isProd ? null : isLocal ? "dev" : window.location.hostname?.split(".")[0];
  const adminUrl = isProd ? "admin.storyboard.com" : `${stage}.admin.storyboardtesting.com`;

  return (
    <Stack spacing={2} sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Paper sx={{ p: 2.5 }}>
        {/* Title row */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h5" fontWeight={600}>
              Item Debug
            </Typography>
            <StatusChip status={item.status} deletedAt={item.deletedAt} />
          </Stack>

          <Stack direction="row" spacing={0.5}>
            <Tooltip title="View in Admin">
              <IconButton
                component={Link}
                to={`https://${adminUrl}/content/${item.contentId}`}
                target="_blank"
                size="small"
              >
                <Icons.OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={showRawData ? "Hide Raw Data" : "Show Raw Data"}>
              <IconButton onClick={() => setShowRawData(!showRawData)} size="small">
                <Icons.DataObject fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* IDs grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 2,
            mb: 2.5,
          }}
        >
          <IdField label="Item ID" value={item.id} />
          <IdField label="Content ID" value={item.contentId} />
          <IdField label="Feed ID" value={item.feedId} />
          <IdField label="Account ID" value={item.accountId ?? "—"} />
          {item.groupId && <IdField label="Group ID" value={item.groupId} />}
          {authorMembership?.id && <IdField label="Author Member ID" value={authorMembership.id} />}
        </Box>

        {/* Properties */}
        <Box sx={{ mb: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              textTransform: "uppercase",
              fontSize: "0.65rem",
              letterSpacing: "0.05em",
              display: "block",
              mb: 1,
            }}
          >
            Properties
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 1,
              fontSize: "0.75rem",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.loadedContent ? theme.palette.success.main : theme.palette.text.disabled,
                }}
              />
              <Typography variant="caption" color={item.loadedContent ? "text.primary" : "text.secondary"}>
                loadedContent
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.isSilent ? theme.palette.warning.main : theme.palette.text.disabled,
                }}
              />
              <Typography variant="caption" color={item.isSilent ? "text.primary" : "text.secondary"}>
                isSilent
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.unread ? theme.palette.info.main : theme.palette.text.disabled,
                }}
              />
              <Typography variant="caption" color={item.unread ? "text.primary" : "text.secondary"}>
                unread
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.isFromAliasMember ? theme.palette.info.main : theme.palette.text.disabled,
                }}
              />
              <Typography variant="caption" color={item.isFromAliasMember ? "text.primary" : "text.secondary"}>
                isFromAliasMember
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.isFromBot ? theme.palette.info.main : theme.palette.text.disabled,
                }}
              />
              <Typography variant="caption" color={item.isFromBot ? "text.primary" : "text.secondary"}>
                isFromBot
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.fromAppSync ? theme.palette.info.main : theme.palette.text.disabled,
                }}
              />
              <Typography variant="caption" color={item.fromAppSync ? "text.primary" : "text.secondary"}>
                fromAppSync
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.firstInsert ? theme.palette.info.main : theme.palette.text.disabled,
                }}
              />
              <Typography variant="caption" color={item.firstInsert ? "text.primary" : "text.secondary"}>
                firstInsert
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.hasRegisteredSeenEvent
                    ? theme.palette.success.main
                    : theme.palette.text.disabled,
                }}
              />
              <Typography variant="caption" color={item.hasRegisteredSeenEvent ? "text.primary" : "text.secondary"}>
                hasRegisteredSeenEvent
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Timestamps */}
        <Stack
          direction="row"
          spacing={3}
          flexWrap="wrap"
          useFlexGap
          sx={{
            pt: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Created {new Date(item.createdAt).toLocaleString()}
          </Typography>
          {item.updatedAt && (
            <Typography variant="caption" color="text.secondary">
              Updated {new Date(item.updatedAt).toLocaleString()}
            </Typography>
          )}
          {item.deletedAt && (
            <Typography variant="caption" color="error">
              Deleted {new Date(item.deletedAt).toLocaleString()}
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Raw Data Panel */}
      {showRawData && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">Raw Item Data</Typography>
            <CopyButton text={JSON.stringify(item, null, 2)} label="JSON" />
          </Stack>
          <Box
            component="pre"
            sx={{
              backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
              p: 2,
              borderRadius: 1,
              overflow: "auto",
              maxHeight: 400,
              fontSize: "0.75rem",
              fontFamily: "monospace",
              m: 0,
            }}
          >
            {JSON.stringify(item, null, 2)}
          </Box>
        </Paper>
      )}

      {/* Content Artifacts */}
      <Stack spacing={1}>
        <SectionAccordion title="Audio Encodings" count={audio.length} defaultExpanded>
          {audio.length > 0 ? (
            <AudioEncodingsTable item={item} />
          ) : (
            <Typography color="text.secondary">No audio encodings</Typography>
          )}
        </SectionAccordion>

        <SectionAccordion title="Transcriptions" count={transcriptions.length} defaultExpanded>
          {transcriptions.length > 0 ? (
            <TranscriptionsTable item={item} />
          ) : (
            <Typography color="text.secondary">No transcriptions</Typography>
          )}
        </SectionAccordion>

        <SectionAccordion title="Files" count={files.length} defaultExpanded={files.length > 0}>
          {files.length > 0 ? <ItemFilesTable item={item} /> : <Typography color="text.secondary">No files</Typography>}
        </SectionAccordion>

        <SectionAccordion title="Links" count={links.length} defaultExpanded={links.length > 0}>
          {links.length > 0 ? <ItemLinksTable item={item} /> : <Typography color="text.secondary">No links</Typography>}
        </SectionAccordion>

        {accountEvents?.length > 0 && (
          <SectionAccordion
            title="Account Events"
            count={accountEvents.length}
            defaultExpanded={accountEvents.length > 0}
          >
            <AccountEventsTable events={accountEvents} />
          </SectionAccordion>
        )}
      </Stack>
    </Stack>
  );
}
